import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  BIBLE_QUIZ_QUESTIONS,
  type BibleQuestion,
  type BibleQuizCategory,
  type QuizGameMode,
} from '@gamengayxua/shared';
import { api } from '../../api/client.ts';

interface BibleQuizGameProps {
  onBackToHub: () => void;
}

// Procedural Web Audio Sound Effects
function playCorrectChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
    osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
    osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.46);
  } catch {}
}

function playWrongBuzz() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(180, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
  } catch {}
}

function playTowerAscendFanfare() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.setValueAtTime(554.37, ctx.currentTime + 0.08);
    osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.16);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.24);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.52);
  } catch {}
}

const CATEGORIES: { id: BibleQuizCategory | 'all'; label: string; icon: string }[] = [
  { id: 'all', label: 'Tất Cả', icon: '🌟' },
  { id: 'cuu_uoc', label: 'Cựu Ước', icon: '📜' },
  { id: 'tan_uoc', label: 'Tân Ước', icon: '🕊️' },
  { id: 'bi_tich', label: 'Các Bí Tích', icon: '⛪' },
  { id: 'phung_vu', label: 'Phụng Vụ', icon: '🕯️' },
  { id: 'cac_thanh', label: 'Các Thánh', icon: '👑' },
];

export const BibleQuizGame: React.FC<BibleQuizGameProps> = ({ onBackToHub }) => {
  const [mode, setMode] = useState<QuizGameMode>('practice');
  const [selectedCategory, setSelectedCategory] = useState<BibleQuizCategory | 'all'>('all');

  // --- PRACTICE MODE STATE ---
  const [practiceIndex, setPracticeIndex] = useState<number>(0);
  const [practiceSelected, setPracticeSelected] = useState<number | null>(null);
  const [practiceScore, setPracticeScore] = useState<number>(0);
  const [practiceTotal, setPracticeTotal] = useState<number>(0);

  // --- SPEED CHALLENGE STATE (10 Questions with Timer) ---
  const [challengeQuestions, setChallengeQuestions] = useState<BibleQuestion[]>([]);
  const [challengeIndex, setChallengeIndex] = useState<number>(0);
  const [challengeSelected, setChallengeSelected] = useState<number | null>(null);
  const [challengeScore, setChallengeScore] = useState<number>(0);
  const [challengeCombo, setChallengeCombo] = useState<number>(0);
  const [challengeTimeLeft, setChallengeTimeLeft] = useState<number>(20);
  const [challengeFinished, setChallengeFinished] = useState<boolean>(false);
  const timerRef = useRef<any>(null);

  // --- TOWER CLIMB STATE ---
  const [towerFloor, setTowerFloor] = useState<number>(1);
  const [towerLives, setTowerLives] = useState<number>(3);
  const [towerQuestion, setTowerQuestion] = useState<BibleQuestion | null>(null);
  const [towerSelected, setTowerSelected] = useState<number | null>(null);
  const [towerGameOver, setTowerGameOver] = useState<boolean>(false);
  const [towerHighestFloor, setTowerHighestFloor] = useState<number>(() => {
    try {
      return parseInt(localStorage.getItem('bible_tower_highest') || '1', 10);
    } catch {
      return 1;
    }
  });

  // Filter questions for practice
  const filteredPracticeQuestions = useMemo(() => {
    if (selectedCategory === 'all') return BIBLE_QUIZ_QUESTIONS;
    return BIBLE_QUIZ_QUESTIONS.filter((q) => q.category === selectedCategory);
  }, [selectedCategory]);

  const currentPracticeQ = filteredPracticeQuestions[practiceIndex % filteredPracticeQuestions.length];

  // Helper to get random subset
  const getRandomQuestions = (count: number): BibleQuestion[] => {
    const shuffled = [...BIBLE_QUIZ_QUESTIONS].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Helper to pick tower question matching floor difficulty
  const pickTowerQuestion = (floor: number): BibleQuestion => {
    let diff: 'easy' | 'medium' | 'hard' = 'easy';
    if (floor > 6) diff = 'hard';
    else if (floor > 3) diff = 'medium';

    const pool = BIBLE_QUIZ_QUESTIONS.filter((q) => q.difficulty === diff);
    const chosen = pool.length > 0 ? pool : BIBLE_QUIZ_QUESTIONS;
    return chosen[Math.floor(Math.random() * chosen.length)];
  };

  // Start / Reset Speed Challenge
  const startSpeedChallenge = () => {
    const qs = getRandomQuestions(10);
    setChallengeQuestions(qs);
    setChallengeIndex(0);
    setChallengeSelected(null);
    setChallengeScore(0);
    setChallengeCombo(0);
    setChallengeTimeLeft(20);
    setChallengeFinished(false);
  };

  // Start / Reset Tower Climb
  const startTowerClimb = () => {
    setTowerFloor(1);
    setTowerLives(3);
    setTowerSelected(null);
    setTowerGameOver(false);
    setTowerQuestion(pickTowerQuestion(1));
  };

  // Timer effect for Speed Challenge
  useEffect(() => {
    if (mode === 'speed_challenge' && !challengeFinished && challengeSelected === null) {
      timerRef.current = setInterval(() => {
        setChallengeTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleChallengeTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [mode, challengeIndex, challengeSelected, challengeFinished]);

  const handleChallengeTimeout = () => {
    playWrongBuzz();
    setChallengeSelected(-1); // Timeout
    setChallengeCombo(0);
  };

  // Handle Practice Answer
  const handlePracticeAnswer = (idx: number) => {
    if (practiceSelected !== null) return;
    setPracticeSelected(idx);
    setPracticeTotal((prev) => prev + 1);
    if (idx === currentPracticeQ.correctIndex) {
      setPracticeScore((prev) => prev + 1);
      playCorrectChime();
    } else {
      playWrongBuzz();
    }
  };

  const handlePracticeNext = () => {
    setPracticeSelected(null);
    setPracticeIndex((prev) => (prev + 1) % filteredPracticeQuestions.length);
  };

  // Handle Speed Challenge Answer
  const handleChallengeAnswer = (idx: number) => {
    if (challengeSelected !== null || challengeFinished) return;
    clearInterval(timerRef.current);
    setChallengeSelected(idx);

    const q = challengeQuestions[challengeIndex];
    if (idx === q.correctIndex) {
      const bonus = challengeCombo >= 2 ? 150 : 100;
      setChallengeScore((prev) => prev + bonus + challengeTimeLeft * 5);
      setChallengeCombo((prev) => prev + 1);
      playCorrectChime();
    } else {
      setChallengeCombo(0);
      playWrongBuzz();
    }
  };

  const handleChallengeNext = () => {
    if (challengeIndex + 1 < challengeQuestions.length) {
      setChallengeIndex((prev) => prev + 1);
      setChallengeSelected(null);
      setChallengeTimeLeft(20);
    } else {
      setChallengeFinished(true);
      api.saveGameProgress('bible-quiz', { score: challengeScore, mode: 'speed_challenge' }, `Thử Thách Thánh Kinh: ${challengeScore} điểm`).catch(() => {});
    }
  };

  // Handle Tower Climb Answer
  const handleTowerAnswer = (idx: number) => {
    if (towerSelected !== null || towerGameOver || !towerQuestion) return;
    setTowerSelected(idx);

    if (idx === towerQuestion.correctIndex) {
      playTowerAscendFanfare();
      const nextFloor = towerFloor + 1;
      setTowerFloor(nextFloor);
      if (nextFloor > towerHighestFloor) {
        setTowerHighestFloor(nextFloor);
        try {
          localStorage.setItem('bible_tower_highest', nextFloor.toString());
        } catch {}
      }
    } else {
      playWrongBuzz();
      const nextLives = towerLives - 1;
      setTowerLives(nextLives);
      if (nextLives <= 0) {
        setTowerGameOver(true);
        api.saveGameProgress('bible-quiz', { floor: towerFloor, mode: 'tower_climb' }, `Leo Tháp Thánh Kinh: Tầng ${towerFloor}`).catch(() => {});
      }
    }
  };

  const handleTowerNext = () => {
    setTowerSelected(null);
    setTowerQuestion(pickTowerQuestion(towerFloor));
  };

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-amber-400 tracking-wide flex items-center space-x-1.5">
            <span>📖</span>
            <span>ĐỐ VUI THÁNH KINH</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30">
            GIÁO LÝ & BÍ TÍCH
          </span>
        </div>
      </div>

      {/* Mode Navigation Tabs */}
      <div className="grid grid-cols-3 gap-1.5 bg-slate-900/90 p-1.5 rounded-xl border border-slate-800 text-xs sm:text-sm font-bold">
        <button
          onClick={() => setMode('practice')}
          className={`py-2 px-1 rounded-lg transition-all ${
            mode === 'practice'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          📚 Học Hỏi
        </button>
        <button
          onClick={() => { setMode('speed_challenge'); startSpeedChallenge(); }}
          className={`py-2 px-1 rounded-lg transition-all ${
            mode === 'speed_challenge'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          ⚡ Thử Thách 10 Câu
        </button>
        <button
          onClick={() => { setMode('tower_climb'); startTowerClimb(); }}
          className={`py-2 px-1 rounded-lg transition-all ${
            mode === 'tower_climb'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          🏰 Leo Tháp
        </button>
      </div>

      {/* =========================================================================
          MODE 1: HỌC HỎI & KHÁM PHÁ (PRACTICE)
          ========================================================================= */}
      {mode === 'practice' && currentPracticeQ && (
        <div className="space-y-3">
          {/* Category Chips Bar */}
          <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
            {CATEGORIES.map((cat) => (
              <button
                key={`cat_${cat.id}`}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setPracticeIndex(0);
                  setPracticeSelected(null);
                }}
                className={`px-3 py-1.5 rounded-lg whitespace-nowrap font-bold transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-amber-500 text-slate-950 shadow-md font-black'
                    : 'bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800'
                }`}
              >
                <span>{cat.icon}</span> {cat.label}
              </button>
            ))}
          </div>

          {/* Question Card */}
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl p-4 sm:p-5 space-y-4 shadow-xl">
            <div className="flex items-center justify-between text-xs">
              <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 font-bold border border-amber-500/20">
                {currentPracticeQ.categoryName} • {currentPracticeQ.difficulty === 'easy' ? 'Dễ' : currentPracticeQ.difficulty === 'medium' ? 'Vừa' : 'Nâng Cao'}
              </span>
              <span className="text-slate-400 font-medium">
                Đúng: <strong className="text-emerald-400">{practiceScore}</strong> / {practiceTotal}
              </span>
            </div>

            <h3 className="text-base sm:text-lg font-bold text-slate-100 leading-snug">
              {currentPracticeQ.question}
            </h3>

            {/* 4 Choices */}
            <div className="space-y-2 pt-1">
              {currentPracticeQ.options.map((opt, idx) => {
                const isSelected = practiceSelected === idx;
                const isCorrect = idx === currentPracticeQ.correctIndex;

                let btnStyle = 'bg-slate-800 hover:bg-slate-700/80 border-slate-700 text-slate-200';
                if (practiceSelected !== null) {
                  if (isCorrect) {
                    btnStyle = 'bg-emerald-600/30 border-emerald-500 text-emerald-200 font-bold ring-2 ring-emerald-500/50';
                  } else if (isSelected) {
                    btnStyle = 'bg-rose-600/30 border-rose-500 text-rose-200 ring-2 ring-rose-500/50';
                  } else {
                    btnStyle = 'bg-slate-900/60 border-slate-800 text-slate-500 opacity-60';
                  }
                }

                return (
                  <button
                    key={`opt_${idx}`}
                    onClick={() => handlePracticeAnswer(idx)}
                    disabled={practiceSelected !== null}
                    className={`w-full p-3 sm:p-3.5 rounded-xl border text-left text-sm transition-all active:scale-98 flex items-center justify-between ${btnStyle}`}
                  >
                    <span>{opt}</span>
                    {practiceSelected !== null && isCorrect && <span className="text-emerald-400 text-base">✓</span>}
                    {practiceSelected !== null && isSelected && !isCorrect && <span className="text-rose-400 text-base">✗</span>}
                  </button>
                );
              })}
            </div>

            {/* Explanation Box */}
            {practiceSelected !== null && (
              <div className="p-3.5 rounded-xl bg-amber-950/20 border border-amber-500/30 space-y-2 animate-fadeIn">
                <div className="flex items-center justify-between text-xs font-bold text-amber-300">
                  <span>📖 Lời giải thích & Trích dẫn:</span>
                  {currentPracticeQ.scriptureRef && (
                    <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-200 font-mono text-[11px]">
                      {currentPracticeQ.scriptureRef}
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
                  {currentPracticeQ.explanation}
                </p>

                <div className="pt-2">
                  <button
                    onClick={handlePracticeNext}
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-sm shadow-lg active:scale-95 transition-all"
                  >
                    Câu Tiếp Theo →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================================
          MODE 2: THỬ THÁCH 10 CÂU (SPEED CHALLENGE)
          ========================================================================= */}
      {mode === 'speed_challenge' && (
        <div>
          {!challengeFinished && challengeQuestions.length > 0 ? (
            <div className="space-y-3">
              {/* Progress and Timer Strip */}
              <div className="bg-slate-900/90 px-3 py-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs sm:text-sm font-bold">
                <div className="flex items-center space-x-2">
                  <span className="text-slate-400">Câu {challengeIndex + 1}/10</span>
                  {challengeCombo >= 2 && (
                    <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-black border border-rose-500/30 animate-pulse">
                      🔥 COMBO x{challengeCombo}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <span className="text-slate-400">Thời gian:</span>
                    <span className={`font-mono text-base font-black ${challengeTimeLeft <= 5 ? 'text-rose-400 animate-ping' : 'text-amber-300'}`}>
                      {challengeTimeLeft}s
                    </span>
                  </div>
                  <span className="text-amber-400 font-black">
                    {challengeScore} điểm
                  </span>
                </div>
              </div>

              {/* Current Question */}
              <div className="bg-slate-900 border border-slate-700/80 rounded-2xl p-4 sm:p-5 space-y-4 shadow-xl">
                <h3 className="text-base sm:text-lg font-bold text-slate-100 leading-snug">
                  {challengeQuestions[challengeIndex].question}
                </h3>

                <div className="space-y-2">
                  {challengeQuestions[challengeIndex].options.map((opt, idx) => {
                    const isSelected = challengeSelected === idx;
                    const isCorrect = idx === challengeQuestions[challengeIndex].correctIndex;

                    let btnStyle = 'bg-slate-800 hover:bg-slate-700/80 border-slate-700 text-slate-200';
                    if (challengeSelected !== null) {
                      if (isCorrect) {
                        btnStyle = 'bg-emerald-600/30 border-emerald-500 text-emerald-200 font-bold';
                      } else if (isSelected) {
                        btnStyle = 'bg-rose-600/30 border-rose-500 text-rose-200';
                      } else {
                        btnStyle = 'bg-slate-900/60 border-slate-800 text-slate-500 opacity-60';
                      }
                    }

                    return (
                      <button
                        key={`ch_opt_${idx}`}
                        onClick={() => handleChallengeAnswer(idx)}
                        disabled={challengeSelected !== null}
                        className={`w-full p-3 sm:p-3.5 rounded-xl border text-left text-sm transition-all active:scale-98 flex items-center justify-between ${btnStyle}`}
                      >
                        <span>{opt}</span>
                        {challengeSelected !== null && isCorrect && <span className="text-emerald-400 font-bold">✓</span>}
                      </button>
                    );
                  })}
                </div>

                {challengeSelected !== null && (
                  <div className="pt-2">
                    <button
                      onClick={handleChallengeNext}
                      className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm shadow-lg active:scale-95 transition-all"
                    >
                      {challengeIndex + 1 < 10 ? 'Câu Kế Tiếp →' : 'Xem Kết Quả Tổng Kết 🏆'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Speed Challenge Finished Summary */
            <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 text-center space-y-4 shadow-2xl">
              <div className="text-4xl">🏆</div>
              <h3 className="text-xl font-black text-amber-300">HOÀN THÀNH THỬ THÁCH!</h3>
              <div className="text-3xl font-black text-white">{challengeScore} Điểm</div>
              <p className="text-xs text-slate-300">
                Chúc mừng bạn đã hoàn thành trọn vẹn 10 câu hỏi thử thách tốc độ Thánh Kinh và các Bí Tích!
              </p>
              <div className="flex justify-center space-x-3 pt-2">
                <button
                  onClick={startSpeedChallenge}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow active:scale-95"
                >
                  Thử Thách Lại
                </button>
                <button
                  onClick={() => setMode('practice')}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm active:scale-95"
                >
                  Học Hỏi Tự Do
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* =========================================================================
          MODE 3: LEO THÁP THÁNH KINH (TOWER CLIMB)
          ========================================================================= */}
      {mode === 'tower_climb' && (
        <div>
          {!towerGameOver && towerQuestion ? (
            <div className="space-y-3">
              {/* Tower Header */}
              <div className="bg-slate-900/90 px-3.5 py-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs sm:text-sm font-bold">
                <div className="flex items-center space-x-2">
                  <span className="text-base">🏰</span>
                  <span className="text-amber-300 font-black">TẦNG {towerFloor}</span>
                  <span className="text-[11px] text-slate-400">(Kỷ lục: Tầng {towerHighestFloor})</span>
                </div>

                <div className="flex items-center space-x-1 text-base">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <span key={`heart_${i}`}>{i < towerLives ? '❤️' : '🖤'}</span>
                  ))}
                </div>
              </div>

              {/* Tower Question Card */}
              <div className="bg-slate-900 border border-slate-700/80 rounded-2xl p-4 sm:p-5 space-y-4 shadow-xl">
                <div className="flex items-center justify-between text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 font-bold border border-amber-500/20">
                    Thử Thách Tầng {towerFloor}
                  </span>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-slate-100 leading-snug">
                  {towerQuestion.question}
                </h3>

                <div className="space-y-2">
                  {towerQuestion.options.map((opt, idx) => {
                    const isSelected = towerSelected === idx;
                    const isCorrect = idx === towerQuestion.correctIndex;

                    let btnStyle = 'bg-slate-800 hover:bg-slate-700/80 border-slate-700 text-slate-200';
                    if (towerSelected !== null) {
                      if (isCorrect) {
                        btnStyle = 'bg-emerald-600/30 border-emerald-500 text-emerald-200 font-bold ring-2 ring-emerald-500/50';
                      } else if (isSelected) {
                        btnStyle = 'bg-rose-600/30 border-rose-500 text-rose-200 ring-2 ring-rose-500/50';
                      } else {
                        btnStyle = 'bg-slate-900/60 border-slate-800 text-slate-500 opacity-60';
                      }
                    }

                    return (
                      <button
                        key={`tw_opt_${idx}`}
                        onClick={() => handleTowerAnswer(idx)}
                        disabled={towerSelected !== null}
                        className={`w-full p-3 sm:p-3.5 rounded-xl border text-left text-sm transition-all active:scale-98 flex items-center justify-between ${btnStyle}`}
                      >
                        <span>{opt}</span>
                        {towerSelected !== null && isCorrect && <span className="text-emerald-400 font-bold">✓</span>}
                      </button>
                    );
                  })}
                </div>

                {towerSelected !== null && (
                  <div className="pt-2">
                    <button
                      onClick={handleTowerNext}
                      className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm shadow-lg active:scale-95 transition-all"
                    >
                      Tiếp Tục Leo Lên Tầng {towerFloor} ⬆️
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Tower Game Over */
            <div className="bg-slate-900 border border-rose-500/40 rounded-2xl p-6 text-center space-y-4 shadow-2xl">
              <div className="text-4xl">🏰💔</div>
              <h3 className="text-xl font-black text-rose-400">KẾT THÚC LƯỢT LEO THÁP</h3>
              <p className="text-sm text-slate-200">
                Bạn đã dừng chân tại <strong className="text-amber-300">Tầng {towerFloor}</strong>.
              </p>
              <p className="text-xs text-slate-400">
                Kỷ lục cao nhất của bạn: Tầng {towerHighestFloor}.
              </p>
              <div className="flex justify-center space-x-3 pt-2">
                <button
                  onClick={startTowerClimb}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow active:scale-95"
                >
                  Leo Lại Từ Đầu
                </button>
                <button
                  onClick={() => setMode('practice')}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm active:scale-95"
                >
                  Về Học Hỏi
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
